import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaRegBell } from "react-icons/fa";
import { FiPhone } from "react-icons/fi";
import { MdOutlineMail } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api";
import Sidebar from "./Sidebar";
import Header from "./Header";
import PageTitle from "./PageTitleCompoent";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";
import profileImg from "../assets/profile_img.png";

import "../css/CustomerDetailScreen.css";
import PawAnimation from "./PawAnimation";
const CustomerDetailScreen = ({ onStatusChange: onStatusChangeProp }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const BASE_URL = api.defaults.baseURL.replace("/api/auth", "");
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);

  const handleBack = () => navigate(-1);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/admin/customers/${state.id}/detail/`);
        setCustomerData(response.data);
        setIsActive(response.data.customer_profile.account_status === "active");
        setLoading(false);
      } catch (err) {
        setError(err.message || "Failed to load customer details");
        setLoading(false);
        toast.error("Failed to load customer details");
      }
    };

    if (state?.id) {
      fetchCustomerDetails();
    }
  }, [state?.id]);

const handleStatusToggle = async () => {
  const newStatus = !isActive;
  setIsActive(newStatus);

  // if (newStatus) {
  //   toast.success("Customer is now Active");
  // } else {
  //   toast.warning("Customer is now Inactive");
  // }

  if (onStatusChangeProp && typeof onStatusChangeProp === "function") {
    onStatusChangeProp(state.id, newStatus);
  }

  try {
    await api.patch(`/customers/`, {
      user_id: state.id,
      status: newStatus ? "active" : "inactive",
    });
    if (newStatus) {
      toast.success("Customer is now Active");
    } else {
      toast.warning("Customer is now Inactive");
    }
  } catch (err) {
    setIsActive(!newStatus); // rollback
    toast.error("Failed to update status");
    console.error("Status toggle failed:", err.response || err.message);
  }
};


  const handleSendNotification = () => {
    toast.info("Notification sent to the customer.");
  };

  const handleDeleteClick = () => {
    setCustomerToDelete(customerData?.customer_profile);
    setIsModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsModalOpen(false);
    setCustomerToDelete(null);
  };
const handleConfirmDelete = async () => {
  if (!customerToDelete) return;

  try {
    // ✅ Call delete API
    const response = await api.delete(`/customers/${customerToDelete.id}/`);
    console.log("Delete response:", response.data);

    // ✅ Remove customer from UI if you have a list
    setCustomers((prev) =>
      prev.filter((c) => String(c.id) !== String(customerToDelete.id))
    );

    // ✅ Close the modal
    handleCloseDeleteModal();

    // ✅ Show success message
    toast.success(
      `Deleted customer: ${customerToDelete.fullName || "unknown"}`
    );

    // ✅ Navigate back
    navigate(-1);

  } catch (err) {
    console.error("Failed to delete customer:", err);
    toast.error("Failed to delete customer. Please try again.");
  }
};


  if (loading) {
    return (
      <div className="full-screen-loader">
        <PawAnimation />
      </div>
    );
  }

  if (error || !customerData) {
    return (
      <div className="container">
        <Sidebar />
        <div className="main">
          <Header />
          <div className="customer-detail-container">
            <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
              {error || "No customer data available"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const {
    customer_profile,
    service_bookings,
    appointment_bookings,
    orders,
    subscription,
    pet_specific_data,
  } = customerData;

  // Get unique service types from recent bookings
  const serviceTypes = [
    ...new Set(
      service_bookings.recent_bookings?.map((b) => b.service_type) || []
    ),
  ];
  const recentServiceBooking = service_bookings.recent_bookings?.[0];

  // Get recent appointment
  const recentAppointment =
    appointment_bookings.recent_completed_appointments?.[0];

  // Dynamic right side content data
// 🕒 Helper functions for consistency
const formatDate = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();

  // Function to add ordinal suffix
  const getOrdinal = (n) => {
    if (n > 3 && n < 21) return "th"; // 4-20 -> th
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${day}${getOrdinal(day)} ${month} ${year}`;
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  // handle both "HH:mm" and "HH:mm:ss" inputs safely
  return new Date(`1970-01-01T${timeStr}`).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // 👈 AM/PM format
  });
};

// 🧩 Main content data
const rightContentData = [
  {
    title: "Service Booking",
    status: service_bookings.completed_bookings > 0 ? "completed" : "pending",
    tags: serviceTypes.slice(0, 3),
    recentBooking: recentServiceBooking
      ? `${recentServiceBooking.service_type} ${formatDate(
          recentServiceBooking.preferred_date
        )} ${formatTime(recentServiceBooking.preferred_time)}`
      : "No recent bookings",
    amount: `₹ ${service_bookings.total_amount?.toLocaleString() || 0}`,
  },
{
  title: "Appointment Booking",
  status:
    appointment_bookings.completed_appointments > 0
      ? "completed"
      : "pending",
  tags: appointment_bookings.recent_completed_appointments.map(
    a => `${a.service_type} (${a.appointment_type})`
  ),

  recentBooking: recentAppointment
    ? `Doctor Appointment ${formatDate(recentAppointment.date)} ${formatTime(recentAppointment.time)}`
    : "No recent appointments",
  amount: `₹ ${appointment_bookings.total_amount?.toLocaleString() || 0}`,
  doctor: appointment_bookings.last_served_doctor
    ? {
        name: appointment_bookings.last_served_doctor.name,
        image: appointment_bookings.last_served_doctor.image || profileImg,
      }
    : null,
}
,
  {
    title: "Orders Placed by the Customer",
    noOfOrders: orders.total_orders || 0,
    amount: `₹ ${orders.total_amount?.toLocaleString() || 0}`,
  },
  {
    title: "Subscription Alert",
    warningText: subscription.needs_notification
      ? `${customer_profile.fullName}'s PawFriends ${
          subscription.current_plan?.duration || ""
        } subscription plan is due for renewal. Please send a notification.`
      : `${customer_profile.fullName} has an active ${
          subscription.current_plan?.duration || ""
        } subscription.`,
    isNotification: subscription.needs_notification,
    isDelete: true,
  },
];


  return (
    <div className="container">
      <Sidebar />
      <div className="main">
        <Header />
        <div className="customer-detail-container">
          <PageTitle title="Customer Profile" onBack={handleBack} />
          <div className="profile-box">
            {/* Left: Customer Details */}
            <div className="profile-left">
              <div
                className={`subscription-badge ${
                  customer_profile.is_subscribed ? "subscribed" : "unsubscribed"
                }`}
              >
                {customer_profile.is_subscribed
                  ? "Subscribed User"
                  : "Unsubscribed User"}
              </div>
              <div className="profile-content">
                <div className="customer-profile-details-status-toggle">
                  <label className="customer_switch">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={handleStatusToggle}
                    />
                    <span className="customer_slider round" />
                  </label>
                  <span
                    className={`status-label ${
                      isActive ? "active" : "inactive"
                    }`}
                  >
                    {isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                <img
                  src={
                    customer_profile.profile_image
                      ? customer_profile.profile_image.startsWith("http")
                        ? customer_profile.profile_image
                        : `${BASE_URL}${customer_profile.profile_image}`
                      : profileImg
                  }
                  alt={customer_profile.fullName}
                  className="customer_avatar"
                />
                <h3>{customer_profile.fullName}</h3>
                <p>No Of Pets They Have: {customer_profile.pets_count}</p>
                <div className="profile_icons">
                  <FiPhone /> {customer_profile.phone_number}
                </div>
                <div className="profile_icons">
                  <MdOutlineMail /> {customer_profile.email}
                </div>
              </div>
            </div>

            {/* Right: Dynamic Info Cards */}
            <div className="profile-right">
              {rightContentData.map((item, index) => (
                <div
                  key={index}
                  className={`info-card ${
                    item.title === "Orders Placed by the Customer"
                      ? "info-card-3"
                      : ""
                  }`}
                >
                  <div className="service-booking-title">{item.title}</div>

                  {item.status && <h4>{item.status}</h4>}

                  {item.tags && item.tags.length > 0 && (
                    <div className="tags">
                      {item.tags.map((tag, idx) => (
                        <div key={idx} className="activity">
                          {tag}
                        </div>
                      ))}
                    </div>
                  )}

                  {item.recentBooking && (
                    <div className="service-content">
                      <strong>Recently Booked</strong>
                      <p>{item.recentBooking}</p>
                      <p className="highlight-red">
                        Overall Service Booking Amount: {item.amount}
                      </p>

                      {item.doctor && (
                        <div className="appointed_doctor">
                          <img
                            src={item.doctor.image}
                            alt={item.doctor.name}
                            className="doctor_img"
                          />
                          <p>{item.doctor.name}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {item.noOfOrders !== undefined && (
                    <div className="service-content">
                      <p>No.of Orders: {item.noOfOrders}</p>
                      <p className="highlight-red">
                        Overall Orders Amount: {item.amount}
                      </p>
                    </div>
                  )}

                  {item.warningText && (
                    <div className="service-content">
                      <p className="warning-text">{item.warningText}</p>
                      {item.isNotification && (
                        <div
                          className="send-btn"
                          onClick={handleSendNotification}
                          style={{ cursor: "pointer" }}
                        >
                          <FaRegBell /> Send Notification
                        </div>
                      )}
                      {item.isDelete && (
                        <div className="delete-account">
                          <span>
                            Delete {customer_profile.fullName} Account?
                          </span>
                          <button
                            className="delete-btn"
                            onClick={handleDeleteClick}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <h2 className="section-divider"></h2>

          {/* Pet Profiles */}
       {/* Pet Profiles */}
{pet_specific_data?.map((pet) => {
  const recentService = pet.services.recent_services?.[0];
  const recentPetAppointment = pet.appointments.recent_appointments?.[0];

  return (
    <div key={pet.pet_id}>
      <h2>Pet Profile</h2>
      <div className="pet-profile-container">
        <div className="profile-left">
          <div className="subscription-badge">{pet.pet_name}</div>
          <div className="pet-profile-content">
<img
  src={
    pet.pet_image
      ? pet.pet_image.startsWith("http")
        ? pet.pet_image
        : `${BASE_URL}${pet.pet_image}`
      : profileImg
  }
  alt={pet.pet_name}
  className="avatar"
/>
            <h3>{pet.pet_name}</h3>
            <p>{pet.pet_category}</p>
            <p>{pet.pet_breed}</p>
            <p>Gender: {pet.pet_gender}</p>
          </div>
        </div>

        <div className="profile-right">
          {/* 🟢 Service Booking Card */}
          <div className="info-card">
            <div className="service-booking-title">Service Booking</div>
            <h4>
              {pet.services.completed_services > 0 ? "completed" : "pending"}
            </h4>
            <div className="tags">
              {[...new Set(pet.services.recent_services?.map((s) => s.service_type) || [])]
                .slice(0, 3)
                .map((type, idx) => (
                  <div key={idx} className="activity">
                    {type}
                  </div>
                ))}
            </div>

            <div className="service-content">
              <strong>Recently Booked</strong>
              <p>
                {recentService
                  ? `${recentService.service_type} — ${formatDate(
                      recentService.date
                    )} at ${formatTime(recentService.time)}`
                  : "No recent services"}
              </p>
              <p className="highlight-red">
                Overall Service Booking Amount: ₹{" "}
                {pet.services.total_amount?.toLocaleString() || 0}
              </p>
            </div>
          </div>

          {/* 🟣 Appointment Booking Card */}
       <div className="info-card">
  <div className="service-booking-title">Appointment Booking</div>
  <h4>
    {pet.appointments.completed_appointments > 0 ? "completed" : "pending"}
  </h4>

<div className="tags">
  {pet.appointments.recent_appointments
    .slice(0, 3) // limit to first 3 appointments
    .map((a, idx) => (
      <div key={idx} className="activity">
        {`${a.service_type} (${a.appointment_type})`}
      </div>
    ))}
</div>



  <div className="service-content">
    <strong>Recently Booked</strong>
    <p>
      {pet.appointments.recent_appointments.length > 0
        ? `Doctor Appointment — ${formatDate(
            pet.appointments.recent_appointments[0].date
          )} at ${formatTime(
            pet.appointments.recent_appointments[0].time
          )}`
        : "No recent appointments"}
    </p>

    <p className="highlight-red">
      Total Appointments: {pet.appointments.total_appointments}
    </p>

    {appointment_bookings.last_served_doctor && (
      <div className="appointed_doctor">
        <img
          src={
            appointment_bookings.last_served_doctor.image || profileImg
          }
          alt="profileImg"
          className="doctor_img"
        />
        <p>{appointment_bookings.last_served_doctor.name}</p>
      </div>
    )}
  </div>
</div>


          {/* 🟡 Orders Card */}
          <div className="info-card-3">
            <div className="service-booking-title">
              Orders Placed by the Customer
            </div>
            <p>No.of Orders: {pet.orders.total_orders}</p>
            <p className="highlight-red">
              Overall Orders Amount: ₹{" "}
              {pet.orders.total_amount?.toLocaleString() || 0}
            </p>
          </div>

          {/* 🔔 Subscription Renewal Notification */}
          {subscription.needs_notification && (
            <div className="info-card">
              <p className="warning-text">
                {customer_profile.fullName}'s PawFriends{" "}
                {subscription.current_plan?.duration || ""} subscription plan is
                due for renewal. Please send a notification.
              </p>
              <div
                className="send-btn"
                onClick={handleSendNotification}
                style={{ cursor: "pointer" }}
              >
                <FaRegBell /> Send Notification
              </div>
            </div>
          )}
        </div>
      </div>
      <h2 className="section-divider"></h2>
    </div>
  );
})}

        </div>
      </div>

      {/* Confirmation Modal */}
      <DeleteWithConfirmation
        isOpen={isModalOpen}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={`Delete ${customerToDelete?.fullName || "Customer"}?`}
        message="Are you sure you want to delete this customer? This action cannot be undone."
      />

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default CustomerDetailScreen;
